// CardholderDetail.cshtml: one cardholder of the admin's scope — balance, points, profile, card actions, loads,
// recent transactions, and "Set password" (as in legacy: a generated password shown once for staff to copy; or a code
// e-mailed to the cardholder, who chooses the password).
// Added (not in legacy): the identity check kept with the application, and — where the card's processor offers
// them (its capabilities, never its name) — taking money back off the card and closing it for good.
import {
  cardApi,
  cardNumberLine,
  money,
  shortDate,
  type CardAction,
  type CardLimits,
  type CloseReason,
  type Profile,
  type Transaction,
} from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { adminApi, kioskApi, type CardholderKyc, type RevealedKyc } from '../api';
import { useStaff } from '../components/RequireStaff';
import { usePageTitle } from '../components/ConsoleLayout';
import { SET_PASSWORD_CONFIRM, ShownPassword } from '../components/ShownPassword';
import { confirmed, PageHeading, ResultBanner, StatusBadge } from '../components/ui';
import { HiddenNumber } from '../components/HiddenNumber';
import { TwoFactorReset } from '../components/TwoFactorReset';

const isCredit = (type: string) => type === 'Load' || type === 'Refund';

/** LifetimeLoads / LifetimeSpend */
export function lifetimeTotals(transactions: readonly Transaction[]) {
  let loads = 0;
  let spend = 0;
  for (const t of transactions) {
    if (isCredit(t.transactionType)) loads += Math.abs(t.amount);
    else spend += Math.abs(t.amount);
  }
  return { loads, spend };
}

/** FormattedAddress: "line1[, line2], city state postal, country" without the empty parts. */
export function formattedAddress(p: Profile | null | undefined): string | null {
  if (!p) return null;
  const blank = (s: string | null | undefined) => !s || s.trim() === '';
  const line1 = blank(p.addressLine2) ? p.addressLine1 : `${p.addressLine1 ?? ''}, ${p.addressLine2}`;
  const cityState = [p.city, p.stateOrProvince].filter((x) => !blank(x)).join(' ');
  const line2 = [cityState, p.postalCode].filter((x) => !blank(x)).join(' ');
  const formatted = [line1, line2, p.country].filter((x) => !blank(x)).join(', ');
  return formatted.trim() === '' ? null : formatted;
}

const ACTION_DONE: Record<CardAction, string> = {
  activate: 'Card activated.',
  freeze: 'Card locked.',
  unfreeze: 'Card unlocked.',
  reissue: 'Replacement card ordered.',
  'convert-to-physical': 'Physical card requested.',
};
const CONFIRM: Partial<Record<CardAction, string>> = {
  reissue: 'Close this card and order a replacement?',
  'convert-to-physical':
    'Request a physical card? This ships to the address on file and may already be a physical card if this was requested before.',
};
/** W22: a person reads "Locked" for what the processors call suspended or frozen; the other statuses as they are. */
export const cardStatusLabel = (status: string | null | undefined) =>
  status === 'Suspended' || status === 'Frozen' ? 'Locked' : (status ?? '--');

/** "QuestionsRequired" -> "Questions required". */
const kycLabel = (status: string) =>
  ({
    Approved: 'Verified',
    QuestionsRequired: 'Waiting for the cardholder to answer questions',
    DocumentsRequired: 'Waiting for documents',
    UnderReview: 'Under review',
    Denied: 'Not verified',
  })[status] ?? status;

const CLOSE_REASONS: [CloseReason, string][] = [
  ['lost', 'Lost'],
  ['stolen', 'Stolen'],
  ['closed', 'No longer needed'],
];

export const FREEZE_FOR_GOOD_CONFIRM =
  "InterCash cannot unlock a card through the API: only its account manager can reinstate it. Lock this cardholder's card?";

export function CardholderDetailPage() {
  const { id = '' } = useParams();
  const client = useQueryClient();
  const { user, scope } = useStaff();
  const isAdmin = user.role === 'SystemAdmin' || user.role === 'ProgramAdmin';
  const holder = useQuery({ queryKey: ['cardholder', id], queryFn: () => adminApi.cardholder(id) });
  const cardholder = holder.data ?? null;
  usePageTitle(cardholder?.name ?? 'Cardholder');
  const ref = cardholder?.providerUserReference ?? null;
  const points = useQuery({
    queryKey: ['cardholder', id, 'points'],
    queryFn: () => adminApi.pointsBalance(id),
    enabled: !!cardholder,
  });
  const account = useQuery({
    queryKey: ['cardholder', id, 'account'],
    enabled: !!ref,
    queryFn: async () => {
      const snapshot = await cardApi.account(ref!);
      const cardRef = snapshot?.cardReferenceId ?? null;
      const [transactions, profile] = await Promise.all([
        snapshot?.isConnected && cardRef ? cardApi.transactions(ref!, cardRef) : Promise.resolve([]),
        cardApi.profile(ref!),
      ]);
      return { snapshot, transactions, profile };
    },
  });
  // D35: what InterCash will still accept onto this card today (asked for InterCash cards only)
  const limits = useQuery({
    queryKey: ['cardholder', id, 'limits'],
    enabled: !!ref && cardholder?.provider === 'InterCash',
    queryFn: () => cardApi.limits(ref!),
  });
  const [outcome, setOutcome] = useState<{ message: string | null; ok: boolean }>({ message: null, ok: false });
  const [amount, setAmount] = useState('25');
  const [unloadAmount, setUnloadAmount] = useState('');
  const [closeReason, setCloseReason] = useState<CloseReason>('lost');
  const [busy, setBusy] = useState(false);
  const [shownPassword, setShownPassword] = useState<string | null>(null);

  const reload = () => client.invalidateQueries({ queryKey: ['cardholder', id] });
  const snapshot = account.data?.snapshot ?? null;
  const connected = snapshot?.isConnected === true;
  const cardRef = snapshot?.cardReferenceId ?? null;

  async function run(work: () => Promise<{ success: boolean; message: string }>) {
    setBusy(true);
    try {
      const r = await work();
      setOutcome({ ok: r.success, message: r.message });
      await reload();
    } finally {
      setBusy(false);
    }
  }

  /** QBalance off (the card reference number leaked: it cannot be changed) or back on. */
  function setWebAccess(enabled: boolean) {
    const id = cardRef?.split(':')[1] ?? cardRef;
    if (!id) return;
    if (!enabled && !confirmed('Switch QBalance off for this card? Whoever is signed in is signed out at once.'))
      return;
    void run(async () => {
      const r = await kioskApi.setWebAccess(id, enabled);
      return {
        success: r.ok,
        message: r.ok ? (enabled ? 'QBalance switched on.' : 'QBalance switched off.') : r.error,
      };
    });
  }

  function act(action: CardAction) {
    // W11: InterCash has no unfreeze; staff are told before the freeze, not after
    const question =
      action === 'freeze' && cardholder?.provider === 'InterCash' ? FREEZE_FOR_GOOD_CONFIRM : CONFIRM[action];
    if (question && !confirmed(question)) return;
    if (!ref || !cardRef) return;
    void run(async () => {
      const r = await cardApi.cardAction(ref, action, cardRef);
      return { success: r.success, message: r.success ? ACTION_DONE[action] : r.message };
    });
  }

  function load(e: FormEvent) {
    e.preventDefault();
    const value = Number(amount);
    if (!ref || !cardRef) return;
    void run(async () => {
      const r = await cardApi.load(ref, {
        cardReferenceId: cardRef,
        amount: value,
        currency: snapshot?.currency ?? 'CAD',
      });
      return {
        success: r.success,
        // done: the amount; waiting for a second admin, refused or unanswered: what the API said
        message:
          r.success && r.message === '' ? `${money(value, snapshot?.currency)} loaded onto the card.` : r.message,
      };
    });
  }

  function unload(e: FormEvent) {
    e.preventDefault();
    const value = Number(unloadAmount);
    if (!ref || !(value > 0)) return;
    const currency = snapshot?.currency ?? 'USD';
    if (!confirmed(`Take ${money(value, currency)} ${currency} off this card?`)) return;
    void run(async () => {
      const r = await cardApi.unload(ref, { amount: value, currency });
      if (r.success) setUnloadAmount('');
      return {
        success: r.success,
        message: r.success && r.message === '' ? `${money(value, currency)} taken off the card.` : r.message,
      };
    });
  }

  function closeCard() {
    if (!ref) return;
    const label = CLOSE_REASONS.find(([r]) => r === closeReason)?.[1] ?? closeReason;
    if (!confirmed(`Close this card for good (${label.toLowerCase()})? It cannot be reopened.`)) return;
    void run(async () => {
      const r = await cardApi.close(ref, closeReason);
      return { success: r.success, message: r.success ? 'Card closed.' : r.message };
    });
  }

  async function setPassword() {
    if (!cardholder || !confirmed(SET_PASSWORD_CONFIRM(cardholder.email))) return;
    setBusy(true);
    const r = await adminApi.setCardholderPassword(cardholder.id);
    setBusy(false);
    if (r.ok) setShownPassword(r.data.password);
    setOutcome({ ok: r.ok, message: r.ok ? 'A new password was set.' : r.error });
  }

  async function setNewPassword(e: FormEvent) {
    e.preventDefault();
    if (!cardholder) return;
    // Or the cardholder chooses the password: they get a code by e-mail.
    if (!confirmed(`E-mail ${cardholder.email} a code to set a new password?`)) return;
    setBusy(true);
    const ok = await adminApi.sendPasswordCode(cardholder.id);
    setBusy(false);
    setOutcome({
      ok,
      message: ok
        ? `A code to set a new password was e-mailed to ${cardholder.email}.`
        : "Couldn't send a password code to this cardholder.",
    });
  }

  if (holder.isPending) return null;
  const name = cardholder?.name ?? 'Cardholder';
  const totals = lifetimeTotals(account.data?.transactions ?? []);
  const p = points.data ?? null;
  const pointsCash = p && p.pointsPerUnit > 0 ? p.balance / p.pointsPerUnit : null;

  return (
    <>
      <PageHeading title={name} trail={[['Cardholders', '/cardholders']]} />

      <ResultBanner {...outcome} />

      {!cardholder ? (
        <p className="qc-empty">Cardholder not found.</p>
      ) : (
        <>
          <section className="qc-stat-row">
            <div className="qc-stat-tile qc-stat-tile--accent">
              <span className="qc-stat-label">Available Balance</span>
              <strong className="qc-stat-value">
                {connected ? money(snapshot.availableBalance, snapshot.currency) : '--'}
              </strong>
              <span className="qc-stat-trend">{connected ? snapshot.currency : 'No card yet'}</span>
            </div>
            <div className="qc-stat-tile qc-stat-tile--purple">
              <span className="qc-stat-label">Loyalty Points</span>
              <strong className="qc-stat-value">{p ? p.balance.toLocaleString('en-US') : '--'}</strong>
              <span className="qc-stat-trend">
                {pointsCash !== null ? `≈ ${money(pointsCash, p!.currency)} ${p!.currency}` : 'No points yet'}
              </span>
            </div>
            <div className="qc-stat-tile qc-stat-tile--success">
              <span className="qc-stat-label">Lifetime Loads</span>
              <strong className="qc-stat-value">{connected ? money(totals.loads, snapshot.currency) : '--'}</strong>
            </div>
            <div className="qc-stat-tile qc-stat-tile--red">
              <span className="qc-stat-label">Lifetime Spend</span>
              <strong className="qc-stat-value">{connected ? money(totals.spend, snapshot.currency) : '--'}</strong>
            </div>
          </section>

          <div className="qc-detail-row">
            <div className="qc-panel">
              <div className="qc-panel-title">
                <h3>Profile</h3>
                <span>{cardholder.programName}</span>
              </div>
              <dl className="qc-detail-dl">
                <div>
                  <dt>Name</dt>
                  <dd>{cardholder.name}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{cardholder.email}</dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>{cardholder.phoneNumber ?? '--'}</dd>
                </div>
                <div>
                  <dt>Address</dt>
                  <dd>{formattedAddress(account.data?.profile) ?? '--'}</dd>
                </div>
                <div>
                  <dt>Processor</dt>
                  <dd>{cardholder.provider}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>
                    <StatusBadge status={cardholder.status} />
                  </dd>
                </div>
                <div>
                  <dt>Registered</dt>
                  <dd>{shortDate(cardholder.createdAt)}</dd>
                </div>
              </dl>
              {cardholder.kyc && (
                <IdentityCheck
                  cardholderId={cardholder.id}
                  kyc={cardholder.kyc}
                  mayReveal={user.role === 'SystemAdmin'}
                  mayRetry={isAdmin}
                  onRetried={(ok, message) => {
                    setOutcome({ ok, message });
                    void reload();
                  }}
                />
              )}
              {cardholder.hasSignIn ? (
                <>
                  <form onSubmit={(e) => void setNewPassword(e)} style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    {isAdmin && (
                      <button
                        type="button"
                        className="qc-button-ghost"
                        disabled={busy}
                        onClick={() => void setPassword()}
                      >
                        Set password
                      </button>
                    )}
                    <button type="submit" className="qc-button-ghost" disabled={busy}>
                      E-mail a password code
                    </button>
                  </form>
                  {shownPassword && (
                    <ShownPassword
                      email={cardholder.email}
                      password={shownPassword}
                      onDone={() => {
                        setShownPassword(null);
                        setOutcome({ ok: true, message: "Password copied. It won't be shown again." });
                      }}
                    />
                  )}
                  <TwoFactorReset kind="cardholder" id={cardholder.id} />
                </>
              ) : (
                <p style={{ color: 'var(--qor-muted)', fontSize: '.8rem', margin: '16px 0 0' }}>
                  No QOR sign-in: this card was issued without inviting the cardholder.
                </p>
              )}
            </div>

            <div className="qc-panel">
              <div className="qc-panel-title">
                <h3>Card account</h3>
                <button type="button" className="qc-account-refresh" disabled={busy} onClick={() => void reload()}>
                  Refresh
                </button>
              </div>

              {!connected ? (
                <p className="qc-empty">{snapshot?.message ?? 'No issued card found for this cardholder yet.'}</p>
              ) : (
                <>
                  <dl className="qc-detail-dl">
                    <div>
                      <dt>Balance</dt>
                      <dd>
                        {money(snapshot.availableBalance, snapshot.currency)} {snapshot.currency}
                      </dd>
                    </div>
                    <div>
                      <dt>Card</dt>
                      <dd data-testid="card-number">
                        {cardNumberLine(snapshot, cardholder.provider).text}
                        {cardNumberLine(snapshot, cardholder.provider).isReference && (
                          <small> (InterCash card ID: the card number is not on file)</small>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Card status</dt>
                      <dd>{cardStatusLabel(snapshot.cardStatus)}</dd>
                    </div>
                  </dl>
                  {cardholder.provider === 'InterCash' && <LoadLimits limits={limits.data ?? null} />}

                  <div className="qc-cardholder-actions">
                    {snapshot.cardStatus === 'IssuedInactive' ? (
                      <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => act('activate')}>
                        Activate
                      </button>
                    ) : (
                      <>
                        {snapshot.cardStatus === 'Suspended' && cardholder.provider === 'InterCash' ? (
                          // W11: InterCash has no unfreeze, so no button that could only ever be refused
                          <span className="qc-breadcrumb" role="note">
                            Locked. InterCash reinstates a card only through its account manager.
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="qc-button-ghost"
                            disabled={busy}
                            onClick={() => act(snapshot.cardStatus === 'Suspended' ? 'unfreeze' : 'freeze')}
                          >
                            {snapshot.cardStatus === 'Suspended' ? 'Unlock' : 'Lock'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="qc-button-ghost"
                          disabled={busy}
                          onClick={() => act('reissue')}
                        >
                          Report lost
                        </button>
                        {cardholder.provider === 'InterCash' && (
                          <button
                            type="button"
                            className="qc-button-ghost"
                            disabled={busy}
                            onClick={() => act('convert-to-physical')}
                          >
                            Convert to physical
                          </button>
                        )}
                        {snapshot.capabilities.closeCard && isAdmin && (
                          <span className="qc-close-card">
                            <select
                              aria-label="Why the card is closed"
                              value={closeReason}
                              onChange={(e) => setCloseReason(e.target.value as CloseReason)}
                            >
                              {CLOSE_REASONS.map(([value, label]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>{' '}
                            <button type="button" className="qc-button-ghost" disabled={busy} onClick={closeCard}>
                              Close card
                            </button>
                          </span>
                        )}
                      </>
                    )}
                    {snapshot.kioskCard && (
                      <p className="qc-field-hint">
                        Kiosk card · CRN ••••{snapshot.kioskCard.cardReferenceLast4 ?? '----'} ·{' '}
                        {snapshot.kioskCard.stock === 'sold' ? 'Sold' : 'In stock'} · QBalance{' '}
                        {snapshot.kioskCard.webAccess ? 'on' : 'off'}{' '}
                        {scope.internal && user.role === 'SystemAdmin' && (
                          <button
                            type="button"
                            className="qc-button-ghost"
                            disabled={busy}
                            onClick={() => setWebAccess(!snapshot.kioskCard!.webAccess)}
                          >
                            {snapshot.kioskCard.webAccess ? 'Switch QBalance off' : 'Switch QBalance on'}
                          </button>
                        )}
                      </p>
                    )}
                    <form onSubmit={load} className="qc-cardholder-load-form">
                      <div className="qc-quick-amounts">
                        {[10, 25, 50, 100].map((q) => (
                          <button
                            key={q}
                            type="button"
                            className="qc-quick-amount-chip"
                            onClick={() => setAmount(String(q))}
                          >
                            +{money(q, snapshot.currency).replace(/\.00$/, '')}
                          </button>
                        ))}
                      </div>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        aria-label="Amount to load"
                        id="qc-load-amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                      <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                        Load funds
                      </button>
                    </form>
                    {snapshot.capabilities.unload && isAdmin && (
                      <form onSubmit={unload} className="qc-cardholder-load-form">
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          aria-label="Amount to withdraw"
                          placeholder="Amount"
                          value={unloadAmount}
                          onChange={(e) => setUnloadAmount(e.target.value)}
                        />
                        <button
                          type="submit"
                          className="qc-button-ghost"
                          disabled={busy || !(Number(unloadAmount) > 0)}
                        >
                          Withdraw funds
                        </button>
                      </form>
                    )}
                  </div>

                  <div className="qc-panel-title" style={{ marginTop: 24 }}>
                    <h3>Recent transactions</h3>
                  </div>
                  {(account.data?.transactions ?? []).length === 0 ? (
                    <p className="qc-empty">No activity yet.</p>
                  ) : (
                    <table className="qc-plain-table">
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Amount</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {account.data!.transactions.map((t, i) => (
                          <tr key={t.providerTransactionReference ?? i}>
                            <td>{t.description}</td>
                            <td>{money(t.amount, t.currency ?? snapshot.currency)}</td>
                            <td>{shortDate(t.postedAt) ?? 'Pending'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

/**
 * The identity check kept with the cardholder's application: its status and the SSN's last four digits. A system
 * admin may open the kept numbers (each look is audited); they are shown until the page is left, never stored.
 */
function IdentityCheck({
  cardholderId,
  kyc,
  mayReveal,
  mayRetry,
  onRetried,
}: {
  cardholderId: string;
  kyc: CardholderKyc;
  mayReveal: boolean;
  mayRetry: boolean;
  onRetried: (ok: boolean, message: string) => void;
}) {
  const [revealed, setRevealed] = useState<RevealedKyc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reveal() {
    if (!confirmed('Show this cardholder’s SSN, date of birth and ID number? Your look is recorded.')) return;
    setBusy(true);
    const r = await adminApi.revealKyc(cardholderId);
    setBusy(false);
    if ('error' in r) setError(r.error);
    else setRevealed(r);
  }

  async function retry() {
    setBusy(true);
    const r = await adminApi.retryKyc(cardholderId);
    setBusy(false);
    onRetried(
      r.success,
      r.success ? 'Asked the processor to check the identity again.' : (r.error ?? "Couldn't do that."),
    );
  }

  const open = kyc.status === 'UnderReview' || kyc.status === 'DocumentsRequired';
  return (
    <section aria-label="Identity check" style={{ marginTop: 16 }}>
      <h4 style={{ margin: '0 0 6px' }}>Identity check</h4>
      <dl className="qc-detail-dl">
        <div>
          <dt>Status</dt>
          <dd>
            <StatusBadge status={kycLabel(kyc.status)} good="Verified" />
          </dd>
        </div>
        <div>
          <dt>SSN</dt>
          <dd>{revealed?.ssn ?? (kyc.ssnLast4 ? `\u2022\u2022\u2022-\u2022\u2022-${kyc.ssnLast4}` : '--')}</dd>
        </div>
        {revealed && (
          <>
            <div>
              <dt>Date of birth</dt>
              <dd>{revealed.dateOfBirth}</dd>
            </div>
            <div>
              <dt>ID number</dt>
              <dd>{revealed.idNumber ?? '--'}</dd>
            </div>
          </>
        )}
        <div>
          <dt>Submitted</dt>
          <dd>
            {shortDate(kyc.submittedAt)} ({kyc.provider})
          </dd>
        </div>
      </dl>
      {error && (
        <p className="qc-auth-error" role="alert">
          {error}
        </p>
      )}
      <div className="qc-cardholder-actions">
        {mayReveal && !revealed && (
          <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void reveal()}>
            Show identity numbers
          </button>
        )}
        {mayRetry && open && (
          <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void retry()}>
            Check again
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * What InterCash will still accept onto the card today (D35): a load past these is refused ("Frequency Exceeded").
 * Each figure is hidden until its eye is clicked.
 */
function LoadLimits({ limits }: { limits: CardLimits | null }) {
  if (limits === null || !limits.supported) return null;
  const currency = limits.currency ?? '';
  const amount = (value: number | null) => (value === null ? '--' : `${money(value, currency)} ${currency}`.trim());
  const count = (value: number | null) => (value === null ? '--' : String(value));
  const none = limits.loadsLeft === 0 || limits.amountLeftToLoad === 0;
  return (
    <section aria-label="Load limits today" style={{ marginTop: 12 }}>
      <h4 style={{ margin: '0 0 6px' }}>Load limits today</h4>
      {limits.message !== null ? (
        <p className="qc-empty">InterCash did not give the limits: {limits.message}</p>
      ) : (
        <>
          <dl className="qc-detail-dl">
            <div>
              <dt>Can still be loaded</dt>
              <dd>
                <HiddenNumber value={amount(limits.amountLeftToLoad)} label="amount left to load" />
              </dd>
            </div>
            <div>
              <dt>Loads left</dt>
              <dd>
                <HiddenNumber
                  value={`${count(limits.loadsLeft)} of ${count(limits.dailyLoadFrequencyLimit)}`}
                  label="loads left"
                />
              </dd>
            </div>
            <div>
              <dt>Daily load limit</dt>
              <dd>
                <HiddenNumber value={amount(limits.dailyLoadLimit)} label="daily load limit" />
              </dd>
            </div>
          </dl>
          {none && (
            <p className="qc-auth-error" role="note">
              InterCash will refuse another load onto this card today.
            </p>
          )}
        </>
      )}
    </section>
  );
}
